/**
 * Shared DOCX locator resolution — the ONE dereference layer used by BOTH the
 * "보기" leg (Studio DOCX source viewer, WS-D0b) and the "대조" leg (knowledge-unit
 * source-fidelity gate, WS-D-fid). Pure, browser-safe (no `node:*`), deterministic.
 *
 * Two resolvers, one module (the verification chain requires viewer + gate to share
 * the same dereference semantics):
 *
 *  1. {@link resolveDocxLocator} — BLOCK-granular. Verbatim hoist of the Studio
 *     viewer's fail-closed 3-tier dereference (anchor → paragraph-ordinal →
 *     heading-path substring). Resolves ANY locator string to a single highlightable
 *     block; ambiguous when a heading-path substring hits >1 block (which, for a real
 *     section WITH body paragraphs, it always does — every body block inherits its
 *     heading trail). The viewer consumes this unchanged.
 *
 *  2. {@link resolveDocxSection} — SECTION-granular. Built for the gate: a knowledge
 *     unit's `provenance.locator` names a SECTION ("§8.5.1 [Hook] 수동 거래호출"), and the
 *     gate must scope term checks to that section's block range. Because (1) is
 *     block-granular it is fail-closed-ambiguous for any real section title, so it
 *     cannot section-scope on its own; (2) resolves the section-title token to the
 *     UNIQUE HEADING whose own text carries it, fail-closed on >1 heading (the same
 *     ambiguity PRINCIPLE as the viewer, applied at heading granularity), then
 *     dereferences that heading THROUGH {@link resolveDocxLocator} (anchor tier) so the
 *     block index is produced by the shared resolver — not a forked copy.
 */

import type { DocxBlock, DocxExtraction, DocxParagraph } from "./extract.ts";

// --- Block-granular resolution (WS-D0b viewer; hoisted verbatim) --------------

export type DocxLocatorMatchedBy = "anchor" | "paragraph-ordinal" | "heading-path";

/** One candidate block for an ambiguous heading-path locator. */
export interface DocxLocatorCandidate {
  blockIndex: number;
  anchor: string;
}

export type DocxLocatorResolution =
  | {
      status: "resolved";
      /** Index into `extraction.blocks` (drives pagination + highlight). */
      blockIndex: number;
      anchor: string;
      matchedBy: DocxLocatorMatchedBy;
    }
  | {
      /**
       * A heading-path partial that matched MORE THAN ONE block. Fail-closed: it is a
       * gap VARIANT, never auto-resolved to a confident highlight — the UI must disclose
       * the non-uniqueness and let the operator pick a specific `anchor`. (Anchor and
       * ordinal tiers are unique by construction and never produce this.)
       */
      status: "ambiguous";
      query: string;
      matchedBy: "heading-path";
      candidates: DocxLocatorCandidate[];
      reason: string;
    }
  | { status: "gap"; query: string; reason: string };

function isParagraph(block: DocxBlock): block is DocxParagraph {
  return block.kind === "paragraph";
}

/**
 * Resolve a knowledge-unit locator to a block. Resolution order, most-specific first:
 *   1. an EXACT structural anchor (`body:para:N`, `body:table:N`) — unique,
 *   2. a bare integer → the paragraph with that ordinal — unique,
 *   3. a heading-path PARTIAL (case-insensitive substring of the block's self-inclusive
 *      heading trail) — MAY be non-unique.
 *
 * The heading-path tier is fail-closed on ambiguity: a substring that hits several
 * blocks (common in a real guide where a heading name recurs, e.g. "개요") does NOT
 * silently pick the first — it returns `ambiguous` with every candidate, so the UI can
 * disclose "not unique" instead of a misleading green "Resolved". An unresolved locator
 * returns an explicit `gap` (never a silent no-op).
 *
 * Takes `Pick<DocxExtraction, "blocks">` rather than the whole extraction because that
 * is genuinely all it reads. Widening (not changing) the parameter keeps every existing
 * caller compiling — a full `DocxExtraction` still satisfies it — while letting a caller
 * that has an ordered block list from another normalized source (the Studio viewer's
 * shared docx/hwpx model) dereference through THIS resolver instead of forking a second
 * copy of the tier order. The resolver stays format-agnostic because a block's identity
 * here is only its `anchor`, `kind`, `ordinal` and `headingPath`.
 */
export function resolveDocxLocator(
  extraction: Pick<DocxExtraction, "blocks">,
  rawQuery: string,
): DocxLocatorResolution {
  const query = rawQuery.trim();
  if (query.length === 0) {
    return { status: "gap", query: rawQuery, reason: "Enter a paragraph ordinal, anchor, or heading text." };
  }
  const blocks = extraction.blocks;

  // 1. exact anchor (unique by construction)
  const anchorIndex = blocks.findIndex((b) => b.anchor === query);
  if (anchorIndex >= 0) {
    return { status: "resolved", blockIndex: anchorIndex, anchor: blocks[anchorIndex].anchor, matchedBy: "anchor" };
  }

  // 2. bare paragraph ordinal (unique by construction)
  if (/^\d+$/.test(query)) {
    const ordinal = Number.parseInt(query, 10);
    const idx = blocks.findIndex((b) => isParagraph(b) && b.ordinal === ordinal);
    if (idx >= 0) {
      return { status: "resolved", blockIndex: idx, anchor: blocks[idx].anchor, matchedBy: "paragraph-ordinal" };
    }
    return { status: "gap", query, reason: `No paragraph with ordinal ${ordinal}.` };
  }

  // 3. heading-path partial (case-insensitive) — collect ALL matches, fail closed on >1
  const needle = query.toLowerCase();
  const candidates: DocxLocatorCandidate[] = [];
  blocks.forEach((b, index) => {
    if (isParagraph(b) && b.headingPath.join(" / ").toLowerCase().includes(needle)) {
      candidates.push({ blockIndex: index, anchor: b.anchor });
    }
  });
  if (candidates.length === 1) {
    return {
      status: "resolved",
      blockIndex: candidates[0].blockIndex,
      anchor: candidates[0].anchor,
      matchedBy: "heading-path",
    };
  }
  if (candidates.length > 1) {
    return {
      status: "ambiguous",
      query,
      matchedBy: "heading-path",
      candidates,
      reason: `${candidates.length}개 블록이 일치 — 고유하지 않음`,
    };
  }

  return { status: "gap", query, reason: `No block matches anchor or heading path "${query}".` };
}

// --- Shared match normalization ----------------------------------------------

/**
 * Deterministic match normalization used by section resolution + term checks
 * (NOT by {@link resolveDocxLocator}, which stays a verbatim hoist). Rules, applied
 * identically to haystack and needle:
 *   - whitespace: every Unicode whitespace run → a single ASCII space, ends trimmed
 *     (real headings carry irregular leading/embedded spaces, e.g. " [Hook] useMOTx ["),
 *   - case: casefolded via `toLowerCase()`.
 * No accent folding, no punctuation stripping — a substring test over the two
 * normalized strings, so token order and punctuation still matter (honest, not fuzzy).
 */
export function normalizeForMatch(text: string): string {
  return text.replace(/\s+/gu, " ").trim().toLowerCase();
}

// --- Section-granular resolution (WS-D-fid gate) ------------------------------

export type DocxSectionMatchedBy = "heading-title";

/** A candidate heading for an ambiguous section-title token. */
export interface DocxSectionCandidate {
  /** Index into `extraction.blocks` of the heading paragraph. */
  blockIndex: number;
  /** The heading paragraph's structural anchor (`body:para:N`) — never its text. */
  anchor: string;
}

/** The `[startBlock, endBlock)` block-index range a resolved section spans. */
export interface DocxSectionRange {
  /** Index of the section's heading block (inclusive). */
  startBlock: number;
  /**
   * Exclusive end: the first later block that is a heading of the SAME-or-shallower
   * outline level (a peer/ancestor that opens a new section), else `blocks.length`.
   * Deeper subheadings and body paragraphs remain INSIDE the section.
   */
  endBlock: number;
}

export type DocxSectionResolution =
  | {
      status: "resolved";
      matchedBy: DocxSectionMatchedBy;
      /** Index into `extraction.blocks` of the resolved heading paragraph. */
      headingBlockIndex: number;
      /** The heading paragraph's ordinal (`DocxParagraph.ordinal`). */
      headingOrdinal: number;
      /** The heading paragraph's structural anchor (`body:para:N`). */
      anchor: string;
      /** 0-based OOXML outline level of the resolved heading. */
      headingLevel: number;
      range: DocxSectionRange;
    }
  | {
      /** More than one heading's own text carried the token. Fail-closed (never first-pick). */
      status: "ambiguous";
      token: string;
      matchedBy: DocxSectionMatchedBy;
      candidates: DocxSectionCandidate[];
      reason: string;
    }
  | { status: "gap"; token: string; reason: string };

/** Compute a resolved heading's `[startBlock, endBlock)` section range. */
function sectionRangeFrom(blocks: readonly DocxBlock[], headingIndex: number, level: number): DocxSectionRange {
  let endBlock = blocks.length;
  for (let i = headingIndex + 1; i < blocks.length; i += 1) {
    const b = blocks[i];
    if (isParagraph(b) && b.isHeading && b.headingLevel !== null && b.headingLevel <= level) {
      endBlock = i;
      break;
    }
  }
  return { startBlock: headingIndex, endBlock };
}

/**
 * Resolve a SECTION-TITLE token to the unique heading whose OWN text carries it
 * (case-insensitive substring over {@link normalizeForMatch}-normalized heading text —
 * NOT the whole heading trail, so a body block inheriting the trail can never match).
 *
 * Fail-closed, mirroring the viewer's ambiguity contract at heading granularity:
 *   - exactly ONE heading matches → `resolved` (+ its `[startBlock, endBlock)` range),
 *     with the block index produced by delegating the heading's anchor back through
 *     {@link resolveDocxLocator} (anchor tier) — one shared dereference, no fork;
 *   - MORE THAN ONE heading matches → `ambiguous` (every candidate listed, never a
 *     silent first-pick);
 *   - an empty token or zero heading matches → `gap`.
 *
 * Empty/blank tokens are a `gap` by construction: a locator like "§8.5.2-8.5.3" that is
 * a bare section-number range with NO title text cannot name a section here (honest —
 * the number scheme is not in the document's heading text).
 */
export function resolveDocxSection(extraction: DocxExtraction, token: string): DocxSectionResolution {
  const needle = normalizeForMatch(token);
  if (needle.length === 0) {
    return { status: "gap", token, reason: "Empty section-title token (locator carried no resolvable heading text)." };
  }
  const blocks = extraction.blocks;
  const candidates: DocxSectionCandidate[] = [];
  blocks.forEach((b, index) => {
    if (isParagraph(b) && b.isHeading && normalizeForMatch(b.text).includes(needle)) {
      candidates.push({ blockIndex: index, anchor: b.anchor });
    }
  });

  if (candidates.length === 1) {
    const headingIndex = candidates[0].blockIndex;
    const heading = blocks[headingIndex] as DocxParagraph;
    // Delegate the actual block dereference to the shared block resolver (anchor tier),
    // so the resolved index is produced by resolveDocxLocator, not a parallel lookup.
    const deref = resolveDocxLocator(extraction, heading.anchor);
    const level = heading.headingLevel ?? 0;
    return {
      status: "resolved",
      matchedBy: "heading-title",
      headingBlockIndex: deref.status === "resolved" ? deref.blockIndex : headingIndex,
      headingOrdinal: heading.ordinal,
      anchor: heading.anchor,
      headingLevel: level,
      range: sectionRangeFrom(blocks, headingIndex, level),
    };
  }
  if (candidates.length > 1) {
    return {
      status: "ambiguous",
      token,
      matchedBy: "heading-title",
      candidates,
      reason: `${candidates.length}개 heading이 일치 — 고유하지 않음 (모호 = 실패)`,
    };
  }
  return { status: "gap", token, reason: `No heading title contains "${token}".` };
}
